import * as d3 from "d3";
import { bboxCollide } from "d3-bboxCollide";

export const calculateClusters = (data, labelDict, constants) => {
  const clusters = {};
  data.nodes.forEach((node) => {
    node.labels.forEach((label) => {
      if (!clusters[label]) {
        clusters[label] = [];
      }
      clusters[label].push(node);
    });
  });

  const clusterCenters = {};
  const labels = Object.keys(clusters);
  const radius = Math.min(constants.width, constants.height) / 3;
  const centerX = constants.width / 2;
  const centerY = constants.height / 2;

  labels.forEach((label, index) => {
    const angle = (index / labels.length) * 2 * Math.PI;
    clusterCenters[label] = {
      x: centerX + radius * Math.cos(angle),
      y: centerY + radius * Math.sin(angle),
    };
  });

  return {
    clusters,
    clusterCenters,
  };
};

export const getClusterCenter = (label, data) => {
  let sumX = 0;
  let sumY = 0;
  let count = 0;

  data.nodes.forEach((node) => {
    if (!node.isLabel && node.labels.includes(label)) {
      sumX += node.x;
      sumY += node.y;
      count++;
    }
  });

  return {
    x: sumX / count,
    y: sumY / count,
  };
};

export function linkedNodes(node, dragging, data) {
  let connectedNodes = new Set([node.id]);
  data.links.forEach((link) => {
    if (link.source.id === node.id) connectedNodes.add(link.target.id);
    if (link.target.id === node.id) connectedNodes.add(link.source.id);
  });

  // get connected nodes of connected nodes
  const connectedNodes2 = new Set();
  connectedNodes.forEach((id) => {
    data.links.forEach((link) => {
      if (link.source.id === id) connectedNodes2.add(link.target.id);
      if (link.target.id === id) connectedNodes2.add(link.source.id);
    });
  });

  // get unconnected nodes
  const unconnectedNodes = new Set();
  for (let i = 0; i < data.nodes.length; i++) {
    if (!connectedNodes2.has(data.nodes[i].id)) {
      unconnectedNodes.add(data.nodes[i].id);
    }
  }

  data.nodes.forEach((d) => {
    if (unconnectedNodes.has(d.id)) {
      d.fx = dragging ? d.x : null;
      d.fy = dragging ? d.y : null;
    }
  });
}

// Drag functions
export function dragstarted(e, d, data, simulation) {
  e.sourceEvent.stopPropagation();
  linkedNodes(e.subject, true, data); // Call linkedNodes when a node is dragged
  if (!e.active) simulation.alphaTarget(0.03).restart();
  d.fx = d.x;
  d.fy = d.y;
}

export function dragged(e, d, drag, wasMoved) {
  const scalingFactor = 0.3; // Change this value to control the drag sensitivity
  d.fx += e.dx * scalingFactor;
  d.fy += e.dy * scalingFactor;
  return (drag += 1);
}

export function dragended(
  e,
  d,
  setSidebar,
  setImgRatio,
  opacityToColor,
  wasMoved,
  simulation,
  data
) {
  if (!wasMoved) {
    if (d.imgH && d.imgW) {
      setImgRatio(d.imgH / d.imgW);
    } else {
      setImgRatio(1);
    }
    setSidebar(d);

    // and highlight connected nodes
    d3.selectAll(".node-image").style("opacity", 0.25);
    d3.selectAll(".link").style("opacity", 0.25);
    d3.selectAll(".node-text").style("stroke", opacityToColor(0.25));
    d3.selectAll(".node-text").style("color", opacityToColor(0.25));
    if (!d.img) {
      d3.select("#node_" + d.id)
        .selectAll(".node-text")
        .style("stroke", "#000");
      d3.select("#node_" + d.id)
        .selectAll(".node-text")
        .style("color", "#000");
    } else {
      d3.select("#node_" + d.id)
        .selectAll(".node-image")
        .style("opacity", 1);
    }
  }
}

export function createSimulation(data, constants, svg, clusterCenters) {
  let { textWidth, height, width, imgWidth, imgHeight } = constants;

  const links = svg.selectAll(".link");
  const nodes = svg.selectAll(".node");

  // generate bounding boxes for each node e.g. this is a 20x10 rectangle [[-10,-5],[10,5]]
  data.nodes.forEach((d) => {
    let padding = 20;
    if (d.img) {
      d.bbox = [
        [-d.imgW / 2 - padding, -d.imgH / 2 - padding],
        [d.imgW / 2 + padding, d.imgH / 2 + padding],
      ];
    } else {
      d.bbox = [
        [-textWidth / 2 - padding, -d.textHeight / 2 - padding],
        [textWidth / 2 + padding, d.textHeight / 2 + padding],
      ];
    }
  });

  // set up so d.bbox is passed to the force
  const bboxForce = bboxCollide((d) => d.bbox)
    .strength(1)
    .iterations(20);

  const clusteringForce = () => {
    function force(alpha) {
      alpha *= 1.4; // Adjust strength as needed

      data.nodes.forEach((d) => {
        if (d.labels && d.labels.length > 0) {
          const label = d.labels[0];
          const cluster = clusterCenters[label];
          if (cluster) {
            d.vx -= (d.x - cluster.x) * alpha;
            d.vy -= (d.y - cluster.y) * alpha;
          }
        }
      });
    }
    force.initialize = (_) => (nodes = _);
    return force;
  };

  let simulation = d3
    .forceSimulation(data.nodes)
    .velocityDecay(0.2)
    .force(
      "link",
      d3
        .forceLink(data.links)
        .id((d) => d.id)
        .distance((d) => {
          const sourceIsImage = d.source.img;
          const targetIsImage = d.target.img;
          if (sourceIsImage && targetIsImage) return 100;
          if (sourceIsImage || targetIsImage) return 100;
          return 200;
        })

        .strength(0.3)
    )
    .force("collision", bboxForce)
    .force("clustering", clusteringForce)
    .force(
      "charge",
      d3
        .forceManyBody()
        .strength(width < 650 ? -100 : -80)
        .distanceMax(400)
    )
    .force(
      "collision",
      d3
        .forceCollide()
        .radius((d) => {
          // see if imgW or imgH is larger
          if (d.img) {
            return Math.max(d.imgW, imgHeight) / 2 + 50;
          } else {
            return Math.max(textWidth, d.textHeight) / 2 + 50;
          }
        })
        .strength(1) // Ensure strong collision force
        .iterations(20)
    )
    .force("x", d3.forceX(width / 2).strength(0.1))
    .force("y", d3.forceY(height / 2).strength(0.1))
    .stop();

  // Get node dimensions for bounds checking
  const getNodeHeight = (d) => {
    return d.img ? d.imgH : d.textHeight || 50;
  };

  const getNodeWidth = (d) => {
    return d.img ? d.imgW : textWidth || 50;
  };

  // Increased padding for better visibility
  const padding = 50;

  // simulation.alphaDecay(0.1);
  // alphaTarget(0.01);

  simulation.on("tick", () => {
    nodes.each((d) => {
      const nodeHeight = getNodeHeight(d);
      const nodeWidth = getNodeWidth(d);
      // if overlap with edge, add some randomness

      let old_x = d.x;
      let old_y = d.y;

      // Constrain x position
      d.x = Math.max(
        nodeWidth / 2 + padding,
        Math.min(width - nodeWidth / 2 - padding, d.x)
      );

      // Constrain y position accounting for actual node height
      d.y = Math.max(
        nodeHeight / 2 + padding,
        Math.min(height - (nodeHeight / 2 + padding), d.y)
      );

      console.log(old_x, old_y, d.x, d.y);
    });

    nodes.attr("transform", (d) => `translate(${d.x}, ${d.y})`);

    links
      .attr("x1", (d) => d.source.x)
      .attr("y1", (d) => d.source.y)
      .attr("x2", (d) => d.target.x)
      .attr("y2", (d) => d.target.y);
  });

  // Increase warm-up iterations
  for (let i = 0; i < 500; ++i) {
    simulation.tick();
    data.nodes.forEach((d) => {
      const nodeHeight = getNodeHeight(d);
      const nodeWidth = getNodeWidth(d);
      // if overlap with edge, add some randomness

      // Constrain x position
      d.x = Math.max(
        nodeWidth / 2 + padding,
        Math.min(width - nodeWidth / 2 - padding, d.x)
      );

      // Constrain y position accounting for actual node height
      d.y = Math.max(
        nodeHeight / 2 + padding,
        Math.min(height - (nodeHeight / 2 + padding), d.y)
      );
    });
  }
  // then change node positions, iterate over nodes and move them to the new positions
  // data.nodes.forEach((d) => {
  //   const nodeHeight = getNodeHeight(d);
  //   const nodeWidth = getNodeWidth(d);
  //   // if overlap with edge, add some randomness

  //   let old_x = d.x;
  //   let old_y = d.y;

  //   // Constrain x position
  //   d.x = Math.max(
  //     nodeWidth / 2 + padding,
  //     Math.min(width - nodeWidth / 2 - padding, d.x)
  //   );

  //   // Constrain y position accounting for actual node height
  //   d.y = Math.max(
  //     nodeHeight / 2 + padding,
  //     Math.min(height - (nodeHeight / 2 + padding), d.y)
  //   );

  //   console.log(old_x, old_y, d.x, d.y);
  // });

  nodes.attr("transform", (d) => `translate(${d.x}, ${d.y})`);

  links
    .attr("x1", (d) => d.source.x)
    .attr("y1", (d) => d.source.y)
    .attr("x2", (d) => d.target.x)
    .attr("y2", (d) => d.target.y);

  return simulation;
}
